import IconLists from '@persona-id/design-tokens/icons/ListIcon';
import IconMore from '@persona-id/design-tokens/icons/MoreIcon';
import IconPlus from '@persona-id/design-tokens/icons/PlusIcon';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom-v5-compat';
import { MEDIA_WIDTHS, mediaQuery, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import DisabledProductFeature from 'dashboard/components/DisabledProductFeature';
import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Pagination from 'dashboard/components/Pagination';
import Permissioned from 'dashboard/components/Permissioned';
import Banner from 'dashboard/components-lib/Banner';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import MultiSelect from 'dashboard/components-lib/MultiSelect';
import NullState from 'dashboard/components-lib/NullState';
import SearchInput from 'dashboard/components-lib/SearchInput';
import FormAddListItem from 'dashboard/forms/FormAddListItem';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Backdrop, Container, HeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { LIST_TYPE_NAMES } from 'dashboard/lib/constants/list';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { List, ListAttributes, ListStatus } from 'dashboard/models/list';
import { ListItem, ListItemGeolocation, ListItemStatus } from 'dashboard/models/list-item';
import { ProductFeatureName } from 'dashboard/models/organization';
import { User } from 'dashboard/models/user';
import { ListFilterParams } from 'dashboard/redux/actions/list';
import GeolocationPageContent from 'dashboard/views/DashboardListsShowView/GeolocationPageContent';
import ListItemsTable from 'dashboard/views/DashboardListsShowView/ListItemsTable';
import UpdateListModal from 'dashboard/views/DashboardListsShowView/UpdateListModal';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import useProductFeature from 'lib/hooks/useProductFeature';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel, PaginationParams } from 'lib/interfaces/api';
import { Place } from 'lib/interfaces/place';
import { Permission } from 'lib/permissions';

const LIST_ITEM_STATUS_LABEL_MAP: Record<ListItemStatus, string> = {
  [ListItemStatus.Pending]: 'Pending',
  [ListItemStatus.Active]: 'Active',
  [ListItemStatus.Archived]: 'Inactive',
};

const STATUS_FILTER_OPTIONS = Object.values(ListItemStatus).map((listItemStatus) => ({
  label: LIST_ITEM_STATUS_LABEL_MAP[listItemStatus],
  value: listItemStatus,
}));

const FilterRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 3fr;
  grid-gap: ${SPACINGS.base * 2}px;

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`;

const subheadText = (type: string, fuzzyMatching?: boolean) => {
  const listTypeName = LIST_TYPE_NAMES[type as keyof typeof LIST_TYPE_NAMES];

  return fuzzyMatching
    ? `List type: ${listTypeName} (with fuzzy matching)`
    : `List type: ${listTypeName}`;
};

interface DashboardListsShowViewProps {
  list?: List;
  listItems: ListItem[];
  filters: ListFilterParams;
  users: User[];
  paginationParams: PaginationParams;
  pageSize: number;
  isLoading: boolean;
  sortParams: SortParams;
  addListItem: (values: any) => Promise<void>;
  deleteList: (listId: string) => Promise<void>;
  deleteListItem: (listItemId: string) => Promise<void>;
  updateList: (list: List, attributes: ListAttributes) => Promise<void>;
  handleStatusFilterUpdate: (statuses: ListItemStatus[]) => void;
  handleDrawCircleComplete: (place: Place) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleOmniSearch: (omniQuery: string) => void;
}

const DashboardListsShowView = ({
  list,
  listItems,
  filters,
  users,
  paginationParams,
  pageSize,
  isLoading,
  sortParams,
  addListItem,
  deleteList,
  deleteListItem,
  updateList,
  handleStatusFilterUpdate,
  handleDrawCircleComplete,
  handlePageLinkClick,
  handlePageSizeClick,
  handleSortUpdate,
  handleOmniSearch,
}: DashboardListsShowViewProps) => {
  const { omni } = filters;
  const [listItemToDelete, setListItemToDelete] = useState(undefined);
  const [
    deleteListItemModal,
    { enable: showDeleteListItemModal, disable: hideDeleteListItemModal },
  ] = useToggle(false);
  const [addListItemModal, { enable: showAddListItemModal, disable: hideAddListItemModal }] =
    useToggle(false);
  const [deleteListModal, { enable: showDeleteListModal, disable: hideDeleteListModal }] =
    useToggle(false);
  const [editListModal, { enable: showEditListModal, disable: hideEditListModal }] =
    useToggle(false);

  const [
    addListItemButtonEnabled,
    { enable: enableAddListButtonItem, disable: disableAddListButtonItem },
  ] = useToggle(true);

  const allowUntrackableSources = useProductFeature(
    ProductFeatureName.ListsFacesUntrackableSources,
  );

  const removeListItem = useCallback(
    (listItemId: ListItem) => {
      showDeleteListItemModal();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setListItemToDelete(listItemId);
    },
    [showDeleteListItemModal],
  );

  const handleSubmitAddListItem = useCallback(
    async (values: any) => {
      try {
        await addListItem(values);
        hideAddListItemModal();
      } catch (err) {
        // prevent modal from closing on failure
      }
    },
    [addListItem],
  );

  const cancelDeleteListItem = useCallback(() => {
    hideDeleteListItemModal();
    setListItemToDelete(undefined);
  }, [hideDeleteListItemModal]);

  const confirmDeleteListItem = useCallback(async () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    await deleteListItem(listItemToDelete.id);
  }, [listItemToDelete, cancelDeleteListItem]);

  const cancelDeleteList = useCallback(() => hideDeleteListModal(), [hideDeleteListModal]);
  const cancelEditList = useCallback(() => hideEditListModal(), [hideEditListModal]);

  const confirmDeleteList = useCallback(async () => {
    if (!list) return;
    await deleteList(list.id);
  }, [deleteList, list]);

  const { status } = filters;

  const statusFilterLabelTextMapper = useCallback((values: string[]) => {
    if (values.length === 1) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return STATUS_FILTER_OPTIONS.find((v) => v.value === values[0]).label;
    } else if (values.length === 0 || values.length === STATUS_FILTER_OPTIONS.length) {
      return 'All Statuses';
    } else if (values.length > 1) {
      return `${values.length} Statuses`;
    }
  }, []);

  if (isLoading && list == null) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  } else if (list == null) {
    return (
      <Backdrop>
        <NullState message="We couldn't find that List." backRoute='/dashboard/lists' />
      </Backdrop>
    );
  }

  const listActive = list.attributes.status === ListStatus.Active;
  const showAddItemButton = allowUntrackableSources || list.type !== ApiModel.ListFace;

  const archivedAt = list.attributes['archived-at'];
  const fuzzyMatchingOn =
    list.type === ApiModel.ListName && list.attributes['allow-fuzzy-name-first'];

  const searchBoxPlaceholdersByListType = {
    [ApiModel.ListBrowserFingerprint]: 'Search by browser fingerprint',
    [ApiModel.ListDeviceFingerprint]: 'Search by device fingerprint',
    [ApiModel.ListCountry]: 'Search by country name (e.g. Jamaica) or 2 letter code (e.g. JM)',
    [ApiModel.ListEmailAddress]: 'Search by email address',
    [ApiModel.ListFace]: 'Search by face',
    [ApiModel.ListField]: 'Search by field value',
    [ApiModel.ListGeolocation]: 'Search by geolocation',
    [ApiModel.ListGovernmentIdNumber]: 'Search by government ID',
    [ApiModel.ListIpAddress]: 'Search by IP address. E.g. 192.168.1.1',
    [ApiModel.ListName]: 'Search by first name, last name, or first and last name',
    [ApiModel.ListPhoneNumber]: 'Search by phone number (spaces allowed)',
    [ApiModel.ListString]: 'Search',
    [ApiModel.ListSelfieBackground]: 'Search by selfie background',
  };

  const listItemsFilter = (
    <>
      <Card>
        <Card.Body>
          <FilterRow id='lists-show-table__filters'>
            <div>
              <FormControlLabel>Status</FormControlLabel>
              <MultiSelect
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                value={status}
                selectOptionValues={STATUS_FILTER_OPTIONS}
                labelTextMapper={statusFilterLabelTextMapper}
                onChange={handleStatusFilterUpdate}
                data-test='lists-show-view__select-status'
                size='md'
              />
            </div>
            <SearchInput
              id='list-items-table__search'
              handleInput={handleOmniSearch}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              value={omni}
              placeholder={searchBoxPlaceholdersByListType[list.type] || 'Search'}
              dataTest='list-items-view__search-input'
              size='md'
            />
          </FilterRow>
        </Card.Body>
      </Card>
    </>
  );

  const filterAndTable = () => {
    return (
      <>
        {listActive && listItemsFilter}

        <Card>
          <Card.Body>
            <Card.Title>List items</Card.Title>
            <ListItemsTable
              list={list}
              listActive={listActive}
              listItems={listItems}
              users={users}
              isLoading={isLoading}
              sortParams={sortParams}
              removeListItem={removeListItem}
              handleShowAddListItemModal={showAddListItemModal}
              handleSortUpdate={handleSortUpdate}
            />
            {listItems.length > 0 && (
              <Pagination
                pageSize={pageSize}
                paginationParams={paginationParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
              />
            )}
          </Card.Body>
        </Card>
      </>
    );
  };

  return (
    <>
      <Helmet title={`${list.attributes.name} - Lists`} />
      <InactiveEnvironmentBanner />
      <Backdrop>
        <Container>
          {!listActive && archivedAt && (
            <Banner data-test='lists-show__archived-banner'>
              This list was deactivated on{' '}
              {DateTime.fromISO(archivedAt).toFormat(DATE_AND_TIME_FORMAT)}.
            </Banner>
          )}
          <HeaderContainer>
            <Header>
              <NavBack to='/dashboard/lists' dataTest='lists-show__nav-back'>
                {`${list.attributes.name} / ${subheadText(list.type, fuzzyMatchingOn)}`}
              </NavBack>
              <Permissioned permission={Permission.ListItemWrite}>
                <HStack>
                  {listActive && showAddItemButton && (
                    <Button
                      data-test='lists-show__add-list-item'
                      disabled={!addListItemButtonEnabled}
                      width='auto'
                      onClick={showAddListItemModal}
                      prefixIcon={<IconPlus />}
                      id='lists-show__add-list-item'
                    >
                      Add item
                    </Button>
                  )}
                  <DropdownMenu
                    trigger={
                      <Button variant='basic' width='auto' prefixIcon={<IconMore />}>
                        More
                      </Button>
                    }
                    align='end'
                  >
                    <DropdownMenu.Item
                      onClick={showEditListModal}
                      id='lists-show__update-list'
                      key='lists-show__update-list'
                      variant='basic'
                    >
                      Update list
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      onClick={showDeleteListModal}
                      id='lists-show__delete-list'
                      key='lists-show__delete-list'
                      variant='danger'
                    >
                      Deactivate list
                    </DropdownMenu.Item>
                    <FeatureFlagged featureFlag='global:enable-platform-connection-dashboard'>
                      <DropdownMenu.Item
                        as={Link}
                        to={`/dashboard/platform-connections/${list.id}`}
                      >
                        Connections
                      </DropdownMenu.Item>
                    </FeatureFlagged>
                  </DropdownMenu>
                </HStack>
              </Permissioned>
            </Header>
          </HeaderContainer>
          <Permissioned
            permission={Permission.ListItemView}
            fallback={
              <DisabledProductFeature
                title=''
                prompt='Your user role does not have permission to view List Items. Please contact your Admin to enable permission to view Lists.'
                data-test='list-show-view__no-list-item-view-permission'
                helpLink={
                  <a
                    href='https://support.withpersona.com/hc/en-us/articles/6794596176019-Editing-an-existing-user-s-role'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Learn more about user roles
                  </a>
                }
                popupMessage='Your user role does not have permission to view List Items.'
                icon={<IconLists />}
              />
            }
          >
            {list.type === ApiModel.ListGeolocation ? (
              <GeolocationPageContent
                list={list}
                listItems={listItems as ListItemGeolocation[]}
                users={users}
                isLoading={isLoading}
                addListItemButtonEnabled={addListItemButtonEnabled}
                removeListItem={removeListItem}
                handleShowAddListItemModal={showAddListItemModal}
                handleDrawCircleStart={disableAddListButtonItem}
                handleDrawCircleCancel={enableAddListButtonItem}
                handleDrawCircleComplete={(place: Place) => {
                  enableAddListButtonItem();
                  handleDrawCircleComplete(place);
                }}
                filterSection={listItemsFilter}
                pageSize={pageSize}
                paginationParams={paginationParams}
                sortParams={sortParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
                handleSortUpdate={handleSortUpdate}
              />
            ) : (
              filterAndTable()
            )}

            <ModalWrapper isOpen={deleteListModal} handleCloseModal={cancelDeleteList}>
              <ConfirmationModal
                cancelAction={cancelDeleteList}
                confirmAction={confirmDeleteList}
                header='Deactivate this list?'
                confirmActionText='Deactivate'
              >
                <p>
                  This action is permanent and can’t be undone. You will be able to see this list
                  and its past matches, but it will no longer match on future inquiries.
                </p>
              </ConfirmationModal>
            </ModalWrapper>

            <ModalWrapper isOpen={editListModal} handleCloseModal={cancelEditList}>
              <UpdateListModal list={list} handleClose={hideEditListModal} onSubmit={updateList} />
            </ModalWrapper>

            <ModalWrapper isOpen={addListItemModal} handleCloseModal={hideAddListItemModal}>
              <AddListItemModal
                listType={list.type}
                handleClose={hideAddListItemModal}
                handleSubmitAddListItem={handleSubmitAddListItem}
              />
            </ModalWrapper>

            <ModalWrapper isOpen={deleteListItemModal} handleCloseModal={cancelDeleteListItem}>
              <ConfirmationModal
                cancelAction={cancelDeleteListItem}
                confirmAction={confirmDeleteListItem}
                header='Deactivate this list item?'
                confirmActionText='Deactivate'
              >
                <p>
                  This action is permanent and can’t be undone. You will be able to see this list
                  item and its past matches, but it will no longer match on future inquiries.
                </p>
              </ConfirmationModal>
            </ModalWrapper>
          </Permissioned>
        </Container>
      </Backdrop>
    </>
  );
};

export default DashboardListsShowView;

interface AddListItemModalProps {
  listType: List['type'];
  handleClose: () => void;
  handleSubmitAddListItem: (values: any) => Promise<void>;
}

const AddListItemModal = ({
  listType,
  handleClose,
  handleSubmitAddListItem,
}: AddListItemModalProps) => {
  return (
    <ModalContainer>
      <ModalHeader data-test='add-list-item-modal__header' onClose={handleClose}>
        Add item to list
      </ModalHeader>
      <ModalBody>
        <FormAddListItem
          listType={listType}
          onSubmit={handleSubmitAddListItem}
          handleCancel={handleClose}
        />
      </ModalBody>
    </ModalContainer>
  );
};
